import { useState, useCallback, useEffect, useRef } from 'react'
import { useMatches } from '@tanstack/react-router'
import { toBlob } from 'html-to-image'
import { useAuth } from '../../contexts/AuthContext'
import { renderAnnotations } from './AnnotationCanvas'
import { getRecentConsoleErrors } from './errorCollector'
import { getApiTrace } from '../../lib/apiTrace'
import { getUiTrace } from '../../lib/uiTrace'
import type { Annotation } from './types'

export interface UseFeedbackReturn {
  isOpen: boolean
  isCapturing: boolean
  isRecording: boolean
  screenshot: Blob | null
  video: Blob | null
  open: () => void
  close: () => void
  startRecording: () => void
  stopRecording: () => void
  submit: (comment: string, annotations: Annotation[]) => Promise<void>
}

/** 1x1 transparent PNG, substituted for any image html-to-image cannot fetch. */
const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='

async function captureScreenshot(): Promise<Blob> {
  // Capture what the reporter sees: the viewport at its current scroll
  // position, not the whole document from the top. html-to-image draws a clone
  // of <html> into a foreignObject that does not scroll, so the clone is
  // sized to the viewport and shifted up/left by the scroll offset. Negative
  // margins (not a transform, which would also move `position: fixed`
  // elements like the sidebar, nor `top`, which Chrome ignores on <html>).
  const blob = await toBlob(document.documentElement, {
    width: window.innerWidth,
    height: window.innerHeight,
    style: {
      marginTop: `${-window.scrollY}px`,
      marginLeft: `${-window.scrollX}px`,
    },
    // NEVER enable cacheBust: it appends `?<timestamp>` to every resource URL,
    // and a `blob:` URL with a query string is unfetchable (ERR_FILE_NOT_FOUND).
    // Entity pictures (org logo, avatars) are blob: URLs — see useEntityPicture —
    // so cache busting made the capture fail on every page showing one.
    cacheBust: false,
    pixelRatio: Math.min(window.devicePixelRatio, 1.5),
    // Skip existing canvas elements (e.g. ECharts) — can't be serialized to SVG
    filter: (node) => !(node instanceof HTMLCanvasElement),
    // An image that fails to fetch becomes a transparent pixel instead of an
    // empty src, which would otherwise resolve to the page itself.
    imagePlaceholder: TRANSPARENT_PIXEL,
    // Without a handler, html-to-image rejects the WHOLE capture as soon as a
    // single <img> fails to load. One broken avatar must not cost the report.
    onImageErrorHandler: () => {},
  })
  if (!blob) throw new Error('Screenshot capture returned null')
  return blob
}

/** Composites annotations onto the screenshot, returning a new PNG blob. */
async function compositeAnnotations(
  screenshotBlob: Blob,
  annotations: Annotation[]
): Promise<Blob> {
  if (!annotations.length) return screenshotBlob

  const img = new Image()
  const url = URL.createObjectURL(screenshotBlob)
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Failed to load screenshot for compositing'))
      img.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }

  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)
  renderAnnotations(ctx, annotations, canvas.width, canvas.height, false)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Compositing toBlob returned null'))
    }, 'image/png')
  })
}

export function useFeedback(): UseFeedbackReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [screenshot, setScreenshot] = useState<Blob | null>(null)
  const [video, setVideo] = useState<Blob | null>(null)
  const { accessToken, orgCode, user } = useAuth()
  const matches = useMatches()

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])

  const open = useCallback(() => {
    if (isOpen || isCapturing) return

    // If recording, stop it and open dialog with the video
    if (isRecording) {
      mediaRecorderRef.current?.stop()
      return
    }

    setIsCapturing(true)
    setScreenshot(null)
    setVideo(null)
    setIsOpen(true)
    captureScreenshot()
      .then((blob) => setScreenshot(blob))
      .catch((err) => console.error('Failed to capture screenshot:', err))
      .finally(() => setIsCapturing(false))
  }, [isOpen, isCapturing, isRecording])

  const startRecording = useCallback(async () => {
    try {
      // Close the dialog first
      setIsOpen(false)

      const stream = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: true,
        video: true,
        audio: false,
      } as DisplayMediaStreamOptions & { preferCurrentTab: boolean })
      streamRef.current = stream

      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm',
      })
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' })
        setVideo(blob)
        setIsRecording(false)
        setIsOpen(true)
        // Stop all tracks to release the screen share
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        mediaRecorderRef.current = null
      }

      // Also handle user stopping the share via browser UI
      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state !== 'inactive') recorder.stop()
      }

      recorder.start()
      setIsRecording(true)
    } catch (err) {
      console.error('Failed to start screen recording:', err)
      // User cancelled the permission prompt — reopen the dialog
      setIsOpen(true)
    }
  }, [])

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop()
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setScreenshot(null)
    setVideo(null)
  }, [])

  const submit = useCallback(
    async (comment: string, annotations: Annotation[]) => {
      const formData = new FormData()
      if (video) {
        formData.append('screenshot', video, 'recording.webm')
      } else if (screenshot) {
        const image = await compositeAnnotations(screenshot, annotations)
        formData.append('screenshot', image, 'screenshot.png')
      }
      if (comment) formData.append('comment', comment)
      formData.append('url', window.location.href)
      if (orgCode) formData.append('org_code', orgCode)
      if (annotations.length) formData.append('annotations', JSON.stringify(annotations))
      const lastMatch = matches.at(-1)
      formData.append(
        'context',
        JSON.stringify({
          user_agent: navigator.userAgent,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          device_pixel_ratio: window.devicePixelRatio,
          platform: navigator.platform,
          language: navigator.language,
          reported_at: new Date().toISOString(),
          build_version: __APP_VERSION__,
          user_email: user?.email || '',
          recent_errors: getRecentConsoleErrors(),
          api_calls: getApiTrace(),
          ui_events: getUiTrace(),
          route: lastMatch ? { id: lastMatch.routeId, params: lastMatch.params } : undefined,
        })
      )

      const res = await fetch('/mgmt/report', {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        body: formData,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    [screenshot, video, accessToken, orgCode, user?.email, matches]
  )

  // Keyboard shortcut: Shift+? (key === '?', no Ctrl/Meta/Alt)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target as HTMLElement).tagName
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
        e.preventDefault()
        open()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  // Shake detection for mobile
  useEffect(() => {
    if (!window.DeviceMotionEvent) return
    let lastShakeTime = 0
    const THRESHOLD = 30
    const handler = (e: DeviceMotionEvent) => {
      const acc = e.accelerationIncludingGravity
      if (!acc) return
      const total = Math.abs(acc.x ?? 0) + Math.abs(acc.y ?? 0) + Math.abs(acc.z ?? 0)
      if (total > THRESHOLD) {
        const now = Date.now()
        if (now - lastShakeTime > 2000) {
          lastShakeTime = now
          open()
        }
      }
    }
    window.addEventListener('devicemotion', handler)
    return () => window.removeEventListener('devicemotion', handler)
  }, [open])

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  return { isOpen, isCapturing, isRecording, screenshot, video, open, close, startRecording, stopRecording, submit }
}
